import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import React, { useState } from 'react'
import { FiMinusCircle, FiPlusCircle, FiTrash } from 'react-icons/fi'
import { ListChildComponentProps, VariableSizeList } from 'react-window'
import { listagemProcedimentos } from 'src/api/dominio'
import useListagem from 'src/hooks/useListagem'
import {
  ObjetoDescricaoGenerico,
  ObjetoDescricaoGenericoValor
} from 'src/types'
import { Autocomplete, AutoCompleteCustomProps } from './Autocomplete'

const LISTBOX_PADDING = 8 // px
const QUEBRA_LINHA = 58 //chars
const QUEBRA_LINHA_XS = 36 //chars
function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props
  return React.cloneElement(data[index], {
    style: {
      ...style
    }
  })
}

const OuterElementContext = React.createContext({})

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null)
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true)
    }
  }, [data])
  return ref
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props
    const itemData = React.Children.toArray(children)
    const theme = useTheme()
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true })
    const itemCount = itemData.length
    const itemSize = smUp ? 48 : 48

    const getChildSize = (child: React.ReactNode) => {
      /*       let linhas = 1
      try {
        linhas = (child as any).props.children.key.split('-')[1]
      } catch (error) {
        console.log(error)
      } */

      const itemLength = (child as any).props.children.length

      return itemLength < 40 ? 60 : 30 * Math.ceil(itemLength / 40)
    }

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize
      }
      return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
    }

    const gridRef = useResetCache(itemCount)

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={index => getChildSize(itemData[index])}
            overscanCount={5}
            itemCount={itemCount}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    )
  }
)

const useStyles = makeStyles(theme => ({
  listbox: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0
    }
  },
  table: {
    // minWidth: 650
    width: '100%',
    '& .MuiTableCell-root.MuiTableCell-body': {
      padding: '8px 0 '
    },
    '& th.MuiTableCell-root.MuiTableCell-body': {
      paddingLeft: '12px '
    },
    '& .MuiTableCell-root.MuiTableCell-head': {
      color: 'rgba(0, 0, 0, 0.87)',
      fontWeight: '500',
      lineHeight: '1.5rem',
      background: '#efefef',
      padding: '4px 8px'
    },
    '& .MuiIconButton-root ': {
      fontSize: '1rem'
    }
  },
  centerInput: {
    margin: '0 10px',
    '& input.MuiInputBase-input': {
      textAlign: 'center'
    }
  },
  noBorderBottom: {
    '& .MuiTableCell-root': {
      borderBottom: 'none'
    }
  }
}))

interface CustomProps extends AutoCompleteCustomProps {
  value: ObjetoDescricaoGenericoValor[]
  handleValue: (newValue: ObjetoDescricaoGenericoValor[]) => void
}
export default function AutocompleteVirtualized(props: CustomProps) {
  const { handleValue, options, ...rest } = props
  const {
    loading,
    dados,
    handleTermoPesquisa,
    termoPesquisa,
    pagina,
    qtdPaginas,
    nenhumDado,
    nenhumResultado,
    dadosCarregados,
    carregarListagem
  } = useListagem(100, listagemProcedimentos)

  const classes = useStyles()

  const [termoBusca, setTermoBusca] = useState('')

  function buscarPorTermo(event: React.ChangeEvent<HTMLInputElement>) {
    setTermoBusca(event.target.value)
    if (event.target.value.length > 3) {
      handleTermoPesquisa(event)
    }
  }

  function limparTermoBusca() {
    setTermoBusca('')
  }

  const filteredList: (item) => ObjetoDescricaoGenericoValor[] = item =>
    rest.value.filter((v, i) => v.id !== item.id)

  const removeItem = item => {
    const filtered = filteredList(item)

    handleValue(filtered)
  }

  const addCount = (item: ObjetoDescricaoGenericoValor) => {
    rest.value.forEach(procedimento => {
      if (procedimento.id === item.id) {
        if (
          Number.isInteger(procedimento.quantidade) &&
          procedimento.quantidade >= 1
        ) {
          procedimento.quantidade++
        } else procedimento.quantidade = 1
      }
    })

    handleValue(rest.value)
  }

  const removeCount = (item: ObjetoDescricaoGenericoValor) => {
    rest.value.forEach(procedimento => {
      if (procedimento.id === item.id) {
        if (
          Number.isInteger(procedimento.quantidade) &&
          procedimento.quantidade >= 2
        ) {
          procedimento.quantidade--
        } else procedimento.quantidade = 1
      }
    })

    handleValue(rest.value)
  }

  const setCount = (item: ObjetoDescricaoGenericoValor, count: number) => {
    rest.value.forEach(procedimento => {
      if (procedimento.id === item.id) {
        if (count <= 1) {
          count = 1
        }
        procedimento.quantidade = count
      }
    })
    handleValue(rest.value)
  }

  const theme = useTheme()
  const ehXS = useMediaQuery(theme.breakpoints.down('xs'))

  const optionsProps =
    termoBusca.length > 3 ? (ehXS ? (loading ? [] : dados) : options) : options

  return (
    <>
      <Autocomplete
        loading={loading}
        multiple
        hideSelected
        hideChips
        options={optionsProps}
        ehProcedimento
        buscarPorTermo={buscarPorTermo}
        limparTermoBusca={limparTermoBusca}
        disableListWrap
        classes={classes}
        ListboxComponent={
          ListboxComponent as React.ComponentType<
            React.HTMLAttributes<HTMLElement>
          >
        }
        onChange={(
          event,
          newValue: ObjetoDescricaoGenericoValor[],
          reason,
          details
        ) => {
          handleValue(newValue)
        }}
        /* renderOption={(option, { selected, inputValue }) => {
          let i = 0
          function getKey(op) {
            i++
            let linhas = 1
            try {
              let charsNumber = !!op.descricao ? op.descricao?.length : 0
              charsNumber += 11
              linhas = +!op.descricao
                ? 1
                : Math.ceil(
                  charsNumber / (!ehXS ? QUEBRA_LINHA : QUEBRA_LINHA_XS)
                )
            } catch (e) {
              console.log(e)
            }

            return 'option-' + linhas + '-' + i
          }

          return (
            <React.Fragment key={getKey(option)}>
              {!!option.noOptions ? (
                <Box
                  onClick={e => {
                    e.preventDefault()
                  }}
                >
                  <em>Nenhum resultado para "{option.inputText}"</em>
                </Box>
              ) : !!option.btnAdd ? (
                <Grid container>
                  <Divider />
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => {
                      rest.onClickAddButton(option.inputText)
                    }}
                  >
                    {rest.addButtonProps?.text
                      ? rest.addButtonProps.text
                      : 'Não encontrou? Clique aqui'}
                  </Button>
                </Grid>
              ) : (
                <Box position="relative">
                  <Box pr={2} component="span">
                    <Chip size="small" label={`Cod. ${option.detalhe}`} />
                  </Box>

                  <span>{option.descricao}</span>
                </Box>
              )}
            </React.Fragment>
          )
        }} */
        {...rest}
        id="autocomplete-procedimentos"
      />
      {!!rest.value?.length && (
        <Box mb={4}>
          <Box mt={4} mb={2} flex justifyItems="space-between">
            <Typography variant="h6" component="p">
              Procedimentos
            </Typography>
            <Typography variant="subtitle2" component="span">
              <Chip
                size="small"
                // style={{ marginLeft: 32 }}
                label={`Total ${rest.value.length} `}
              />
            </Typography>
          </Box>
          <Divider />
          <Grid container>
            {rest.value?.map((procedimento, i) => (
              <Grid container justify="center" alignItems="center" key={i}>
                {/* <Grid item xs={8}>
                  <Typography variant="subtitle2" component="p">
                    <span>{`Cod. ${procedimento.detalhe} - ${procedimento.descricao}`}</span>
                  </Typography>
                </Grid> */}
                <Grid item xs={12}>
                  <TableContainer
                    component={Paper}
                    style={{ marginTop: '20px' }}
                  >
                    <Table className={classes.table} aria-label="simple table">
                      {/* <TableHead>
                        <TableRow>
                          <TableCell colSpan={5}>{rest.tableTitle}</TableCell>
                        </TableRow>
                      </TableHead> */}
                      <TableBody>
                        <TableRow className={classes.noBorderBottom}>
                          <TableCell component="th" width="auto" scope="row">
                            {!!procedimento?.detalhe && (
                              <small
                              // style={{
                              //   background: '#ddd',
                              //   borderRadius: '4px',
                              //   padding: '3px'
                              // }}
                              >
                                Cod. {procedimento.detalhe}
                              </small>
                            )}
                            <Typography variant="subtitle2" component="p">
                              <span>{procedimento.descricao}</span>
                            </Typography>
                          </TableCell>
                          <TableCell align="right" width="40px">
                            <IconButton
                              title="Remover item"
                              onClick={() => removeCount(procedimento)}
                            >
                              <FiMinusCircle />
                            </IconButton>
                          </TableCell>
                          <TableCell
                            className={classes.centerInput}
                            width="50px"
                          >
                            <TextField
                              data-cy="qtdOpme"
                              key={`${procedimento.id}-${
                                procedimento.quantidade - Math.random()
                              }`}
                              size="small"
                              label="Qtd"
                              defaultValue={procedimento.quantidade}
                              inputProps={{ min: 1, max: 999 }}
                              onChange={e => {
                                try {
                                  debugger
                                  const number = parseInt(e.target.value)
                                  if (!!Number.isInteger(number)) {
                                    setCount(procedimento, number)
                                    return
                                  }
                                } catch {}

                                setCount(procedimento, 1)
                              }}
                              variant="outlined"
                              type="tel"
                            />
                          </TableCell>
                          <TableCell width={'40px'}>
                            <IconButton
                              title="Adicionar item"
                              onClick={() => addCount(procedimento)}
                            >
                              <FiPlusCircle />
                            </IconButton>
                          </TableCell>
                          <TableCell width="36px">
                            <IconButton
                              className="danger"
                              title="Remover OPME"
                              onClick={() => removeItem(procedimento)}
                            >
                              <FiTrash />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                {/* <Grid item xs={2} style={{ textAlign: 'right' }}>
                  <IconButton
                    className="danger"
                    title="Remover procedimento"
                    onClick={() => removeItem(procedimento)}
                  >
                    <FiTrash />
                  </IconButton>
                </Grid> */}
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  )
}
